import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Divider } from '@factorialco/deprecated-design-system'
import { F0Button as Button, F0Text, ProgressBarChart } from '@factorialco/f0-react'
import { BookOpen, CheckDouble } from '@factorialco/f0-react/icons/app'
import { CreateBlockProgressDocument, LoadContentDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { LMSModuleAvailabilityWall } from 'modules/trainings/components/LMSModuleAvailabilityWall'
import ContentConsumer from 'modules/trainings/components/content/Consumer'
import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import { ContentConsumerCallbacks } from 'modules/trainings/components/content/types'
import { Block } from 'modules/trainings/lib/content/Block'
import { ContentModule } from 'modules/trainings/lib/content/Module'
import { QuizBlock } from 'modules/trainings/lib/content/QuizBlock'
import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'

export default observer(() => {
  const { trainingId } = useParams()
  const { employee: currentEmployee } = useCurrentSession()

  const { data, loading } = useQuery(LoadContentDocument, {
    variables: {
      trainingId: Number(trainingId),
      employeeId: currentEmployee.id,
    },
  })

  const collection = useMemo(() => {
    const modules = data?.trainings.contentModules
    if (loading || !modules) {
      return null
    }
    return graphqlContentMapper.fromGraphQL(modules, Number(trainingId))
  }, [trainingId, data, loading])

  const [createBlockProgress] = useMutation(CreateBlockProgressDocument, {
    extractorFn(mutationData) {
      return mutationData.trainingsMutations.createTrainingsContentBlockProgress
    },
  })

  const consumerRef = useRef<ContentConsumerCallbacks>(null)
  const [nextButtonLabel, setNextButtonLabel] = useState('')
  const [nextButtonEnabled, setNextButtonEnabled] = useState(true)
  const [nextButtonLoading, setNextButtonLoading] = useState(false)
  const updateNextButton = () => {
    const nextLabel = consumerRef.current ? consumerRef.current.nextLabel() : ''
    setNextButtonLabel(nextLabel ? nextLabel : i18n.t('trainings.content.consumer.continue'))

    setNextButtonEnabled(consumerRef.current ? consumerRef.current.nextEnabled() : true)
    setNextButtonLoading(
      consumerRef.current?.nextLoading ? consumerRef.current.nextLoading() : false
    )
  }
  useEffect(() => {
    updateNextButton()
  })

  const isDone = useMemo(() => {
    if (!collection) return false
    const allModulesCompleted = collection.modules.map((contentModule) =>
      contentModule.blocks.every((block) => block.status === 'completed')
    )
    return allModulesCompleted.every((module) => module)
  }, [collection])

  useEffect(() => {
    if (isDone && collection) {
      collection.setSelected(undefined)
      setShowEmptyState(true)
    }
  }, [isDone, collection])

  const [showEmptyState, setShowEmptyState] = useState(false)

  if (!collection || loading) {
    return <Loading expand />
  }

  // Create an async function so the button shows a loading state
  const handleNext = async () => {
    if (collection.selectedItem instanceof Block) {
      const currentBlock = collection.selectedItem
      try {
        // Check block completed before creating the progress in the backend (for example video and quiz)
        if (consumerRef.current && !(await consumerRef.current?.handleNext())) {
          return
        }
        // If the last item is completed, set the selected item to undefined
        if (collection.isLastItem() && isDone) {
          collection.setSelected(undefined)
          setShowEmptyState(true)
        }

        const result = await createBlockProgress({
          variables: {
            contentBlockId: currentBlock.id,
            employeeId: currentEmployee.id,
          },
        })

        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.error'),
          })
        })

        if (handled.success) {
          // If we enter here, the progress exists in backend, so we
          // mark the current block as completed and next block as accesible
          currentBlock.status = 'completed'
          currentBlock.accesible = true

          const items = collection.flatItems
          const currentIndex = items.findIndex((item) => item.uuid === currentBlock.uuid)

          // Helper to check if item should be skipped (quiz without form or module)
          const shouldSkipItem = (item: (typeof items)[number]) => {
            if (item instanceof QuizBlock && !item.form) return true
            if (item instanceof ContentModule) return true
            return false
          }

          // Helper to find next valid block
          const findNextValidBlock = (startIndex: number) => {
            for (let i = startIndex; i < items.length; i++) {
              const item = items[i]
              if (item && !shouldSkipItem(item)) {
                return item
              }
            }
            return null
          }

          if (currentIndex !== -1 && currentIndex < items.length - 1) {
            // Find and mark next valid block as accessible
            const nextValidBlock = findNextValidBlock(currentIndex + 1)
            if (nextValidBlock instanceof Block) {
              nextValidBlock.accesible = true
              collection.setSelected(nextValidBlock.uuid)
            }
          } else {
            collection.setSelected(undefined)
            setShowEmptyState(true)
          }
        }
      } catch (_error) {
        // Mutation has failed
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      }
    } else if (collection.selectedItem instanceof ContentModule) {
      collection.next()
    }
  }

  // Filter out quiz blocks without materialized forms for progress calculation
  const nonModuleItems = collection.flatItems.filter((item) => {
    if (item instanceof ContentModule) return false
    if (item instanceof QuizBlock && !item.form) return false
    return true
  })
  const completedItems = nonModuleItems.filter(
    (item) => item instanceof Block && item.status === 'completed'
  )

  const percentage = Math.round((completedItems.length / nonModuleItems.length) * 100)

  return (
    <LMSModuleAvailabilityWall>
      <ResponsivePage.Body variant="fullscreen">
        <ContentConsumer
          tocItems={collection.modules.map((contentModule) => {
            // Filter out quiz blocks without materialized forms
            const visibleBlocks = contentModule.blocks.filter((block) => {
              // If it's a QuizBlock without a form, hide it
              if (block instanceof QuizBlock && !block.form) {
                return false
              }
              return true
            })

            return {
              id: contentModule.uuid,
              label: contentModule.title,
              icon: BookOpen,
              onClick: () => {
                collection.setSelected(contentModule.uuid)
                setShowEmptyState(false)
              },
              children: visibleBlocks.map((block) => ({
                id: block.uuid,
                label: block.title,
                onClick: () => {
                  collection.setSelected(block.uuid)
                  setShowEmptyState(false)
                },
                disabled: !block.accesible,
                icon:
                  block.status === 'completed' ? CheckDouble : contentRegistry.fetch(block, 'icon'),
              })),
              // Allow empty modules to be clickable, only disable if has blocks but none are accessible
              disabled: visibleBlocks.length > 0 && !visibleBlocks.some((block) => block.accesible),
            }
          })}
          selectedItem={collection.selectedItem}
          updateNextButton={updateNextButton}
          ref={consumerRef}
          showEmptyState={showEmptyState}
        />

        {!showEmptyState && (
          <>
            <Divider />
            <Box
              flexDirection="row"
              justifyContent="spaceBetween"
              alignItems="center"
              gap="s16"
              padding="s16"
              width="full"
              paddingRight="s64"
            >
              <Box width="s200" gap="s8" flexDirection="row" alignItems="center">
                <F0Text
                  content={i18n.t('trainings.content.consumer.progress')}
                  variant="description"
                />
                <Box width="full">
                  <ProgressBarChart
                    value={completedItems.length}
                    max={nonModuleItems.length}
                    label={`${percentage}%`} // Optional label shown on the right
                  />
                </Box>
              </Box>
              <Box gap="s16" flexDirection="row">
                <Button
                  label={i18n.t('trainings.content.consumer.back')}
                  variant="outline"
                  onClick={() => {
                    collection.back()
                  }}
                />
                <Button
                  label={nextButtonLabel}
                  disabled={!nextButtonEnabled}
                  loading={nextButtonLoading}
                  onClick={handleNext}
                />
                {/* Disable based on computed completion on the contentItem */}
              </Box>
            </Box>
          </>
        )}
      </ResponsivePage.Body>
    </LMSModuleAvailabilityWall>
  )
})
